import { Button, Grid, Heading, Input, styled, Text } from '@conteg/ui';
import { PageLayout } from 'components/basic-layout';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from 'utils/routing/route-paths';

import { ContactInput } from './components/contact-input';
import { CourierInput } from './components/courier-input';
import {
  BoxIcon,
  ContactIcon,
  LockerIcon,
  TruckIcon,
} from './components/icons';
import { PointAutocomplete } from './components/point-autocomplete';
import {
  serializeContactFilters,
  serializeContentFilters,
  serializeCourierFilters,
  serializePointFilters,
} from './use-audit-filters';

const TileContainer = styled.div`
  padding: 4rem;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  text-align: center;

  border-radius: 2.5rem;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 8rem;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  svg {
    color: ${({ theme }) => theme.colors.secondary.grey300};
  }
`;

const MaxWidth = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

/**
 * Audit Dashboard - Landing page with 4 tiles for quick access
 */
export function AuditDashboard() {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [selectedPointId, setSelectedPointId] = useState('');

  // Content tile state
  const [trackingIdentifier, setTrackingIdentifier] = useState('');

  // Courier tile state
  const [courierPersonalId, setCourierPersonalId] = useState('');

  // Contact tile state
  const [contact, setContact] = useState('');

  const handlePointSearch = () => {
    const search = serializePointFilters({ pointId: selectedPointId });
    navigate({ pathname: RoutePaths.AuditPoint, search });
  };

  const handleContentSearch = () => {
    const search = serializeContentFilters({
      trackingIdentifier: trackingIdentifier,
    });
    navigate({ pathname: RoutePaths.AuditContent, search });
  };

  const handleCourierSearch = () => {
    const search = serializeCourierFilters({
      courierPersonalId: courierPersonalId,
    });
    navigate({ pathname: RoutePaths.AuditCourier, search });
  };

  const handleContactSearch = () => {
    const search = serializeContactFilters({ contact: contact });
    navigate({ pathname: RoutePaths.AuditContact, search });
  };

  return (
    <PageLayout title={t('Events.PageTitle')}>
      <MaxWidth>
        <Grid
          gridTemplateColumns="repeat(auto-fit, minmax(280px, 1fr))"
          gridGap="4rem"
        >
          {/* Point/StorageUnit Tile */}
          <TileContainer>
            <LockerIcon style={{ width: '48px' }} />
            <Heading variant="h2" title={t('Events.Tabs.Point')} />
            <Text
              variant="small"
              content={t('Events.Tile.Point.Description')}
            />
            <PointAutocomplete
              selectedPointId={selectedPointId}
              onChange={(point) => setSelectedPointId(point?.id ?? '')}
            />
            <Button
              title={t('Events.Button.Search')}
              onClick={handlePointSearch}
            />
          </TileContainer>

          {/* Content Tile */}
          <TileContainer>
            <BoxIcon style={{ width: '48px' }} />

            <Heading variant="h2" title={t('Events.Tabs.Content')} />
            <Text
              variant="small"
              content={t('Events.Tile.Content.Description')}
            />
            <Input
              value={trackingIdentifier}
              placeholder={t('Events.Filter.TrackingIdentifier.Placeholder')}
              label={t('Events.Filter.TrackingIdentifier')}
              onChange={(ev) => setTrackingIdentifier(ev.target.value)}
            />
            <Button
              title={t('Events.Button.Search')}
              onClick={handleContentSearch}
            />
          </TileContainer>

          {/* Courier Tile */}
          <TileContainer>
            <TruckIcon style={{ width: '48px' }} />

            <Heading variant="h2" title={t('Events.Tabs.Courier')} />
            <Text
              variant="small"
              content={t('Events.Tile.Courier.Description')}
            />
            <CourierInput
              personalId={courierPersonalId}
              onPersonalIdChange={setCourierPersonalId}
            />
            <Button
              title={t('Events.Button.Search')}
              onClick={handleCourierSearch}
            />
          </TileContainer>

          {/* Contact Tile */}
          <TileContainer>
            <ContactIcon style={{ width: '48px' }} />
            <Heading variant="h2" title={t('Events.Tabs.Contact')} />
            <Text
              variant="small"
              content={t('Events.Tile.Contact.Description')}
            />
            <ContactInput value={contact} onChange={setContact} />
            <Button
              title={t('Events.Button.Search')}
              onClick={handleContactSearch}
            />
          </TileContainer>
        </Grid>
      </MaxWidth>
    </PageLayout>
  );
}
